import { filtetShpFile } from "@/lib/utils/file";
import {
    faChevronLeft,
    faClose,
    faInfoCircle,
    faMagnifyingGlass,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    addToast,
    Autocomplete,
    AutocompleteItem,
    Button,
    Chip,
    Divider,
    ModalBody,
    ModalHeader,
} from "@heroui/react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ChangeEvent, DragEvent, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import shp, { FeatureCollectionWithFilename } from "shpjs";
import { Key } from "@react-types/shared";
import useMap from "@/lib/map/useMap";
import { LngLatBoundsLike } from "mapbox-gl";
import * as turf from "@turf/turf";
import AddBulkFieldTable from "@/components/field/add-bulk/AddBulkFieldTable";
import { Feature, FeatureCollection } from "geojson";
import useRequest from "@/lib/request/useRequest";
import { Field } from "@/types/field";
import useAuth from "@/lib/auth/useAuth";
import { Organization } from "@/types/organization";
import dayjs from "dayjs";
import InfoModal from "@/components/common/InfoModal";

export const Route = createFileRoute("/dashboard/fields/add-bulk")({
    component: RouteComponent,
});

function RouteComponent() {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [dragActive, setDragActive] = useState(false);

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        handleFileChange({
            target: {
                files: e.dataTransfer.files,
            },
        } as ChangeEvent<HTMLInputElement>);
    };

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(true);
    };

    const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
    };

    const fileUpload = useRef<File>();

    const { t } = useTranslation();

    const [shapefileProperties, setShapefileProperties] = useState<string[]>(
        [],
    );

    const [geojson, setGeojson] = useState<FeatureCollection | undefined>();

    const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files?.[0];
            if (!file) return;

            try {
                fileUpload.current = file;
                const fileData = await filtetShpFile(file);
                const fileGeoJson: FeatureCollectionWithFilename = (await shp(
                    fileData,
                )) as FeatureCollectionWithFilename;
                setShapefileProperties(
                    Object.keys(fileGeoJson?.features[0]?.properties ?? {}),
                );
                const filteredFeatures = fileGeoJson.features
                    .filter(
                        (f) =>
                            f.geometry &&
                            (f.geometry.type === "Polygon" ||
                                f.geometry.type === "MultiPolygon"),
                    )
                    .map((f) => {
                        if (f.geometry.type === "Polygon") {
                            return {
                                ...f,
                                geometry: {
                                    type: "MultiPolygon",
                                    coordinates: [f.geometry.coordinates],
                                },
                            };
                        }
                        return f;
                    });
                setGeojson({
                    type: "FeatureCollection",
                    features: filteredFeatures as Feature[],
                });
            } catch (error) {
                console.error(error);
                addToast({
                    title: t("addToast.invalid_shapefile", {
                        ns: "messages",
                    }),
                    timeout: 5000,
                    color: "danger",
                });
                setShapefileProperties([]);
                setGeojson(undefined);
            }
        }
    };

    const { map, mapLoaded } = useMap();
    const [selectedProperty, setSelectedProperty] = useState<
        Key | null | undefined
    >(null);

    const { useMutateRequest } = useRequest();
    const createFieldsRequest = useMutateRequest<Field[]>({
        url: "/fields/bulk/",
        method: "post",
        successMessage: t("success.fields_were_created", { ns: "messages" }),
        errorMessage: t("error.failed_to_create_fields", { ns: "messages" }),
    });
    const navigate = useNavigate();
    const { isConsultantOrSentinel, user } = useAuth();
    const handleSavePressed = useCallback(
        (features: Feature[]) => {
            if (!selectedProperty) return;
            createFieldsRequest.mutate(
                {
                    data: features.map((feature) => ({
                        ...feature,
                        properties: {
                            grower: isConsultantOrSentinel
                                ? feature.properties?.grower
                                : (user?.organization as Organization)?.id,
                            name: feature.properties?.[selectedProperty],
                        },
                    })),
                },
                {
                    onSuccess: () => {
                        navigate({
                            to: "/dashboard/home/field-config",
                            search: {
                                created_date: dayjs().format("YYYY-MM-DD"),
                            },
                        });
                    },
                },
            );
        },
        [
            createFieldsRequest,
            isConsultantOrSentinel,
            navigate,
            selectedProperty,
            user?.organization,
        ],
    );

    return (
        <div className="h-full flex flex-col p-2 gap-2">
            <div className="flex flex-row gap-2 items-center justify-between">
                <div className="flex flex-row gap-2 items-center">
                    <Button
                        variant="light"
                        isIconOnly
                        as={Link}
                        size="lg"
                        to="/dashboard/fields/add"
                        aria-label="Back to Dashboard"
                    >
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </Button>
                    <div className="font-bold text-3xl">Bulk Field Upload</div>
                </div>
                <InfoModal
                    trigger={(setShowModal) => (
                        <Button
                            onPress={() => setShowModal(true)}
                            size="sm"
                            variant="light"
                            color="primary"
                            className="cursor-pointer"
                            endContent={<FontAwesomeIcon icon={faInfoCircle} />}
                        >
                            File Upload Info
                        </Button>
                    )}
                >
                    <ModalHeader>Bulk Field Upload</ModalHeader>
                    <ModalBody>
                        <ul className="list-disc ml-4">
                            <li>
                                This page allows you to upload a shapefile of
                                field boundaries and create them in bulk.
                            </li>
                            <li>
                                The file must be a zip file containing a
                                shapefile or a single shapefile in a directory.
                            </li>
                            <li>
                                Example allowed zip file structures:
                                <ul className="mt-2 mb-2 ml-4 list-disc text-sm text-gray-700">
                                    <li>
                                        <span className="font-mono bg-gray-100 px-2 py-1 rounded">
                                            myfields.zip
                                        </span>
                                        <ul className="ml-6 mt-1 mb-2 list-none">
                                            <li>
                                                ├──{" "}
                                                <span className="font-mono">
                                                    myfields.shp
                                                </span>
                                            </li>
                                            <li>
                                                ├──{" "}
                                                <span className="font-mono">
                                                    myfields.shx
                                                </span>
                                            </li>
                                            <li>
                                                └──{" "}
                                                <span className="font-mono">
                                                    myfields.dbf
                                                </span>
                                            </li>
                                        </ul>
                                    </li>
                                    <li>
                                        <span className="font-mono bg-gray-100 px-2 py-1 rounded">
                                            myfields.zip
                                        </span>
                                        <ul className="ml-6 mt-1 mb-2 list-none">
                                            <li>
                                                └──{" "}
                                                <span className="font-mono">
                                                    folder/
                                                </span>
                                                <ul className="ml-6 list-none">
                                                    <li>
                                                        ├──{" "}
                                                        <span className="font-mono">
                                                            fields.shp
                                                        </span>
                                                    </li>
                                                    <li>
                                                        ├──{" "}
                                                        <span className="font-mono">
                                                            fields.shx
                                                        </span>
                                                    </li>
                                                    <li>
                                                        └──{" "}
                                                        <span className="font-mono">
                                                            fields.dbf
                                                        </span>
                                                    </li>
                                                </ul>
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </ModalBody>
                </InfoModal>
            </div>

            <div
                className={`w-full  border-2 border-dashed rounded-xl transition-colors duration-200 flex flex-col gap-2 items-center justify-center p-4 bg-white shadow-md ${dragActive
                    ? "border-primary-500 bg-primary-50"
                    : "border-gray-300"
                    }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                style={{ cursor: "pointer" }}
            >
                <div className="flex flex-row gap-2 items-center justify-center">
                    <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        onChange={handleFileChange}
                        accept=".zip"
                    />
                    <div>Drag and drop or </div>
                    <Button
                        onPress={() => fileInputRef.current?.click()}
                        color="primary"
                        size="sm"
                    >
                        Select File
                    </Button>
                </div>
                {fileUpload.current && (
                    <div className="flex flex-row gap-2 items-center">
                        <Chip radius="sm" color="secondary" size="lg">
                            {fileUpload.current.name}
                        </Chip>
                        <Button
                            isIconOnly
                            size="sm"
                            color="danger"
                            onPress={() => {
                                fileUpload.current = undefined;
                                if (fileInputRef.current) {
                                    fileInputRef.current.value = "";
                                }
                                setShapefileProperties([]);
                                setGeojson(undefined);
                                setSelectedProperty(null);
                            }}
                        >
                            <FontAwesomeIcon icon={faClose} />
                        </Button>
                    </div>
                )}
            </div>
            <div className="flex flex-col gap-2">
                {shapefileProperties.length > 0 && (
                    <>
                        <Divider />
                        <div className="font-semibold">Upload Details</div>
                        <div className="flex flex-row gap-2 items-end">
                            <Autocomplete
                                label="Select Field Name Property"
                                placeholder=" "
                                size="sm"
                                labelPlacement="outside"
                                onSelectionChange={setSelectedProperty}
                                selectedKey={selectedProperty}
                                isRequired
                                isInvalid={!selectedProperty}
                            >
                                {shapefileProperties.map((property) => (
                                    <AutocompleteItem key={property}>
                                        {property}
                                    </AutocompleteItem>
                                ))}
                            </Autocomplete>
                            <div>
                                <Button
                                    size="sm"
                                    color="primary"
                                    onPress={() => {
                                        if (!map || !mapLoaded || !geojson)
                                            return;
                                        map.fitBounds(
                                            turf.bbox(
                                                geojson,
                                            ) as LngLatBoundsLike,
                                            {
                                                padding: 20,
                                                duration: 0,
                                            },
                                        );
                                    }}
                                    startContent={
                                        <FontAwesomeIcon
                                            icon={faMagnifyingGlass}
                                        />
                                    }
                                >
                                    Zoom To Fields
                                </Button>
                            </div>
                        </div>
                    </>
                )}
                {geojson && (
                    <>
                        <Divider />
                        <AddBulkFieldTable
                            geojson={geojson}
                            selectedProperty={selectedProperty}
                            onSavePressed={handleSavePressed}
                            isLoading={createFieldsRequest.isPending}
                        />
                    </>
                )}
            </div>
        </div>
    );
}
